import * as moment from 'moment';

const API_TIME_FORMAT = 'HH:mm';
const DISPLAY_TIME_FORMAT = 'hh:mm a';
const SUPPORTED_FORMATS = ['hh:mm a', 'h:mm a', 'h:mma', 'HH:mm', 'H:mm', 'ha', 'h a', 'HH', 'h'];

/**
 * Converts a time string from any supported format to the specified target format.
 *
 * @param {string} time - The input time string to convert. Can be in any of the supported formats.
 * @param {string} targetFormat - The desired output format using moment.js format tokens.
 *
 * @returns {string} The converted time string in the target format, or empty string if input is invalid.
 *
 * @supported_formats
 * - 'hh:mm a' - 12-hour with leading zeros and AM/PM (e.g., '02:00 PM')
 * - 'h:mm a' - 12-hour single digit with AM/PM (e.g., '2:00 PM')
 * - 'h:mma' - 12-hour single digit, no space before AM/PM (e.g., '2:00PM')
 * - 'HH:mm' - 24-hour with leading zeros (e.g., '14:00')
 * - 'H:mm' - 24-hour single digit (e.g., '14:00')
 * - 'ha' - Hour with AM/PM, no space (e.g., '2PM')
 * - 'h a' - Hour with space and AM/PM (e.g., '2 PM')
 * - 'HH' - 24-hour format, just hour with leading zero (e.g., '14')
 * - 'h' - Just single digit hour (e.g., '2')
 */
function convertTimeFormat(time: string, targetFormat: string): string {
  const parsedMoment = moment(time, SUPPORTED_FORMATS, true);

  return parsedMoment.isValid() ? parsedMoment.format(targetFormat) : '';
}

function convertToDisplayTimeFormat(time: string): string {
  return convertTimeFormat(time, DISPLAY_TIME_FORMAT);
}

function convertToApiTimeFormat(time: string): string {
  return convertTimeFormat(time, API_TIME_FORMAT);
}

export const TimeFormatUtils = {
  API_TIME_FORMAT,
  DISPLAY_TIME_FORMAT,
  SUPPORTED_FORMATS,
  convertTimeFormat,
  convertToDisplayTimeFormat,
  convertToApiTimeFormat,
};
